import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import Skeleton from 'react-loading-skeleton';
import { useAsync } from 'react-use';

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import moment from 'moment';
import { cloneDeep, isNaN } from 'lodash';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

import {
  Box,
  IconButton,
  makeStyles,
  Checkbox,
  Tooltip,
} from '@material-ui/core';

import Page from '~/components/shared/Page';
import Header from '~/components/layouts/DashboardLayout/components/Header';
import {
  get3DExplorerOverallStats,
  get3DExplorerLaunchesChartStats,
  getLocationHeatmapStats,
  getExplorer3dById,
  updateBulkExplorer3DUnits,
} from '~/services/api';

import {
  calculateChartSize,
  calculateEventsInIntervals,
  calculateImgSize,
  filterStatsByDate,
  formatLabels,
  roundToTwoDecimalPlaces,
  getCurrencySymbol,
  formatNumberWithCommas,
} from '~/views/private/overview/utils';
import { dateConverterForUnits } from '~/views/private/billings/utils';
import size from '~/utils/size';
import { defaultTimeRange } from '~/utils/defaultTimeRange';
import { CardStat } from '~/views/private/overview/Components/CardStat';
import { trySetRange } from '~/store/datePicker';

import {
  setVisitsTooltipInfo,
  updateVisitsTooltipInfo,
} from '~/store/visitsTooltip';

import { ArrowLeft } from '~/views/private/billings/components/icons/ArrowLeft';
import { Cube } from '~/views/private/overview/Components/icons/Cube';
import { Edit } from '~/views/private/overview/Components/icons/Edit';
import { Build } from '~/views/private/overview/Components/icons/Build';
import { Lotus } from '~/views/private/overview/Components/icons/Lotus';
import { Map } from '~/views/private/overview/Components/icons/Map';
import { Squares } from '~/views/private/overview/Components/icons/Squares';
import { Check } from '~/views/private/overview/Components/icons/Check';
import { Check2 } from '~/views/private/overview/Components/icons/Check2';
import { ChevronRight } from '~/views/private/overview/Components/icons/ChevronRight';
import { LeadIcon2 } from '~/views/private/overview/Components/icons/LeadIcon2';
import { Download } from '~/views/private/overview/Components/icons/Download';
import HeatMap from '~/views/private/overview/Components/HeatMap';
import { ArrowRight } from '~/views/private/billings/components/icons/ArrowRight';
import { formatDateFromSeconds } from '~/utils/helper';
import { MultiDateRangePicker, TimeStep, GraphTooltip } from './Components';
import { TOOLTIP_TYPE } from './constants';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const useStyles = makeStyles(() => ({
  root: {
    minHeight: '100%',
    padding: 'var(--utilities-size-1-500)',
  },
  headerContainer: {
    display: 'flex',
    width: '100%',
    padding:
      'var(--utilities-size-none) var(--utilities-size-none) var(--utilities-size-0-750) var(--utilities-size-none)',
    alignItems: 'center',
    gap: 'var(--utilities-size-1-000)',
    alignSelf: 'stretch',
    borderBottom: '1px solid var(--border-default)',
  },
  headerTextSection: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--utilities-size-1-000)',
    flex: '1 0 0',
  },
  headerButtonsSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500)',
  },
  backButton: {
    display: 'flex',
    width: '52px',
    height: '52px',
    minHeight: 'var(--utilities-size-2-500)',
    padding: 'var(--utilities-size-0-500) var(--utilities-size-1-000)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500)',
    alignSelf: 'stretch',
    borderRadius: 'var(--utilities-border-radius-default)',
    background: 'var(--surface-tint-weak)',
    boxShadow: 'var(--shadows-subtle)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--surface-hover-tint-weak-hover)',
    },
  },
  backButtonIcon: {
    width: 'var(--utilities-size-1-250)',
    height: 'var(--utilities-size-1-250)',
  },
  headerTitleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flex: '1 0 0',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500)',
    alignSelf: 'stretch',
  },
  headerTitleText: {
    color: 'var(--content-secondary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-xl)',
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: 'var(--typography-line-height-xl-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
  headerSubTitleText: {
    color: 'var(--content-secondary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-sm)',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 'var(--typography-line-height-sm-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
  virtualTourButton: {
    display: 'flex',
    minHeight: 'var(--utilities-size-2-500)',
    padding: 'var(--utilities-size-0-500) var(--utilities-size-1-000)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500)',
    borderRadius: 'var(--utilities-border-radius-default)',
    border: '1px solid var(--border-accent-weak)',
    background: 'var(--surface-accent-tint)',
    boxShadow: 'var(--shadows-subtle)',
    cursor: 'pointer',
    '&:hover': {
      border: '1px solid var(--border-hover-accent-weak-hover)',
      background: 'var(--surface-hover-accent-tint-hover)',
    },
  },
  buttonIcon: {
    width: 'var(--utilities-size-1-250)',
    height: 'var(--utilities-size-1-250)',
  },
  editButtonIcon: {
    width: 'var(--utilities-size-1-000)',
    height: 'var(--utilities-size-1-000)',
  },
  virtualTourButtonText: {
    textAlign: 'center',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-md)',
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 'var(--typography-line-height-md-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
  explorerButton: {
    display: 'flex',
    minHeight: 'var(--utilities-size-2-500)',
    padding: 'var(--utilities-size-0-500) var(--utilities-size-1-000)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500)',
    borderRadius: 'var(--utilities-border-radius-default)',
    background: 'var(--surface-accent)',
    boxShadow: 'var(--shadows-subtle)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--surface-hover-accent-hover)',
    },
  },
  explorerButtonText: {
    color: 'var(--content-inverted)',
    textAlign: 'center',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-md)',
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 'var(--typography-line-height-md-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
  leadIcon2: {
    height: 'var(--utilities-size-1-000)',
    width: 'var(--utilities-size-1-000)',
  },
  infoCardContainer: {
    alignSelf: 'stretch',
    paddingTop: 'var(--utilities-size-1-500)',
  },
  infoCardsRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--utilities-size-0-500)',
    alignSelf: 'stretch',
  },
  cardStat: {
    flex: '1 !important',
    flexGrow: '1 !important',
    justifyContent: 'center !important',
    width: 'unset !important',
  },
  designComponentInstanceNode: {
    color: 'var(--content-primary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-lg)',
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: 'var(--typography-line-height-lg-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
  chartHeaderContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '18px 0 20px 0',
  },
  sectionHeader: {
    color: 'var(--content-primary)',
    fontSize: 'var(--typography-font-size-md)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: 'var(--typography-line-height-md-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
  downloadIconContainer: {
    position: 'relative',
    boxShadow: '0px 1px 2px rgba(23, 28, 38, 0.05)',
    borderRadius: 'var(--utilities-border-radius-default)',
    backgroundColor: 'var(--surface-default)',
    border: '1px solid rgba(23, 28, 38, 0.2)',
    boxSizing: 'border-box',
    overflow: 'hidden',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 6px',
    height: 'var(--utilities-size-1-500)',
    marginLeft: 'var(--utilities-size-1-000)',
    '&:hover': {
      cursor: 'pointer',
      border: '1px solid rgba(70, 86, 229, 0.4)',
    },
  },
  downloadIcon: {
    position: 'relative',
    width: 'var(--utilities-size-1-000)',
    height: 'var(--utilities-size-1-000)',
    overflow: 'hidden',
    flexShrink: 0,
  },
  container: {
    height: '370px',
  },
  heatMapContainer: {
    display: 'flex',
    padding: 'var(--utilities-size-1-000)',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 'var(--utilities-size-1-500)',
    alignSelf: 'stretch',
    borderRadius: 'var(--utilities-border-radius-default)',
    boxShadow: 'var(--shadows-subtle)',
  },
  heatMapContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--utilities-size-4-000)',
    alignSelf: 'stretch',
  },
  heatMapTable: {
    display: 'flex',
    // width: '500px',
    width: '40%',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 'var(--utilities-size-none)',
    alignSelf: 'stretch',
    maxHeight: '400px',
    overflowY: 'auto',
    paddingRight: 'var(--utilities-size-0-500)',
  },
  emptyHeatMapTable: {
    display: 'flex',
    padding: 'var(--utilities-size-1-000) var(--utilities-size-none)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500)',
    alignSelf: 'stretch',
    color: 'var(--content-secondary)',
    textAlign: 'center',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-md)',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 'var(--typography-line-height-md-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
  utmTableHeaderCell: {
    color: 'var(--content-secondary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-sm)',
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 'var(--typography-line-height-sm-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
  utmContainer: {
    display: 'flex',
    padding: 'var(--utilities-size-1-000)',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 'var(--utilities-size-1-500)',
    flex: '1 0 0',
    alignSelf: 'stretch',
    borderRadius: 'var(--utilities-border-radius-default)',
    boxShadow: 'var(--shadows-subtle)',
  },
  utmTable: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 'var(--utilities-size-none)',
    alignSelf: 'stretch',
  },
  utmTableRow: {
    display: 'flex',
    padding: 'var(--utilities-size-0-500) var(--utilities-size-none)',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500)',
    alignSelf: 'stretch',
    borderBottom: '1px solid var(--border-rude-weak)',
  },
  footer: {
    display: 'flex',
    width: '100%',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    height: 'var(--utilities-size-3-500)',
    backgroundColor: 'var(--surface-default)',
    borderRadius: 'var(--utilities-border-radius-default)',
    boxShadow: 'var(--shadows-subtle)',
  },
  footerPart1: {
    display: 'flex',
    padding: 'var(--utilities-size-none) var(--utilities-size-1-500)',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flex: '1 0 0',
    alignSelf: 'stretch',
    borderRadius:
      'var(--utilities-border-radius-default) var(--utilities-border-radius-none) var(--utilities-border-radius-none) var(--utilities-border-radius-default)',
    background: 'var(--surface-default)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--surface-hover-default-hover)',
    },
    '& .MuiTouchRipple-root': {
      position: 'unset',
    },
  },
  footerPart2: {
    display: 'flex',
    width: '160px',
    padding: 'var(--utilities-size-none) var(--utilities-size-1-000)',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'stretch',
    borderLeft: '1px solid var(--border-default)',
    background: 'var(--surface-default)',
  },
  pagination: {
    color: 'var(--content-secondary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-sm)',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 'var(--typography-line-height-sm-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
  footerPart3: {
    display: 'flex',
    padding: 'var(--utilities-size-none) var(--utilities-size-1-500)',
    alignItems: 'center',
    flex: '1 0 0',
    alignSelf: 'stretch',
    justifyContent: 'flex-Start',
    borderRadius:
      'var(--utilities-border-radius-none) var(--utilities-border-radius-default) var(--utilities-border-radius-default) var(--utilities-border-radius-none)',
    borderLeft: '1px solid var(--border-default)',
    background: 'var(--surface-default)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--surface-hover-default-hover)',
    },
    '& .MuiTouchRipple-root': {
      position: 'unset',
    },
  },
  sectionContent: {
    width: '100%',
    height: '236px',
    display: 'flex',
    padding: '0 40px',
    justifyContent: 'space-between',
  },
  sectionCard: {
    width: '168px',
    height: '148px',
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    borderRadius: 'var(--utilities-border-radius-default)',
    backgroundColor: 'var(--palette-indigo-600)',
    boxShadow: 'var(--shadows-popover)',
  },
  sectionIconBox: {
    display: 'flex',
    height: '56px',
    padding: 'var(--utilities-size-0-750) 16px',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignSelf: 'stretch',
    borderRadius: 'var(--utilities-border-radius-default)',
  },
  sectionIcon: {
    width: '32px',
    height: '32px',
    flexShrink: '0',
  },
  secondSectionIconBox: {
    width: '16px',
    height: '16px',
    opacity: '0.6',
  },
  sectionBox: {
    display: 'flex',
    width: '168px',
    height: '92px',
    padding: 'var(--utilities-size-0-500) var(--utilities-size-1-000)',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--utilities-size-1-000)',
    alignSelf: 'stretch',
    borderRadius: 'var(--utilities-border-radius-default)',
    border: '2px solid var(--palette-indigo-600)',
    background: 'var(--surface-accent-pale)',
  },
  sectionBoxContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: '4px',
    flex: '1 0 0',
  },
  connection: {
    position: 'absolute',
    left: '227px',
    top: '92px',
    borderTop: '1px solid var(--border-default)',
    width: '100%',
  },
  secondConnection: {
    position: 'absolute',
    left: '227px',
    top: '152px',
    borderTop: '1px solid var(--border-default)',
    width: '100%',
  },
  sectionBoxContentTitle: {
    color: 'var(--palette-indigo-600)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-sm)',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 'var(--typography-line-height-sm-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
  sectionBoxContentBodyCount: {
    height: '28px',
    width: '100%',
    flex: '1 0 0',
    color: 'var(--palette-indigo-600)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-lg)',
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: 'var(--typography-line-height-lg-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
  sectionBoxContentBodyDuration: {
    color: 'var(--palette-indigo-600)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-sm)',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 'var(--typography-line-height-sm-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
    opacity: '0.4',
  },
  unitsSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    alignSelf: 'stretch',
  },
  closeButton: {
    display: 'flex',
    maxHeight: 'var(--utilities-size-2-000, 32px)',
    padding:
      'var(--utilities-size-0-375, 6px) var(--utilities-size-0-500, 8px)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500, 8px)',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
    border: '1px solid var(--border-control-default, rgba(23, 28, 38, 0.20))',
    background: 'var(--surface-default, #FFF)',
    boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--surface-hover-default-hover)',
      border: '1px solid var(--border-hover-control-default-hover, #171c2699)',
    },
  },
  closeButtonText: {
    color: 'var(--content-primary, #2C313A)',
    textAlign: 'center',
    fontFamily: 'var(--typography-font-family-default, Inter) !important',
    fontSize: 'var(--typography-font-size-sm, 14px)',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'var(--typography-line-height-sm-line, 20px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
  },
  saveButton: {
    display: 'flex',
    maxHeight: 'var(--utilities-size-2-000, 32px)',
    padding:
      'var(--utilities-size-0-375, 6px) var(--utilities-size-0-500, 8px)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500, 8px)',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
    background: 'var(--surface-success, #13AE70)',
    boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--surface-hover-success-hover)',
    },
  },
  whiteButtonText: {
    color: 'var(--content-inverted, #FFF)',
    textAlign: 'center',
    fontFamily: 'var(--typography-font-family-default, Inter) !important',
    fontSize: 'var(--typography-font-size-sm, 14px)',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'var(--typography-line-height-sm-line, 20px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
  },
  hidePricesSetting: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500, 8px)',
  },
  hidePricesSettingLabel: {
    color: 'var(--content-primary, #2C313A)',
    fontFamily: 'var(--typography-font-family-default, Inter) !important',
    fontSize: 'var(--typography-font-size-sm, 14px)',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'var(--typography-line-height-sm-line, 20px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
    whiteSpace: 'nowrap',
  },
  manageButton: {
    display: 'flex',
    maxHeight: 'var(--utilities-size-2-000, 32px)',
    padding:
      'var(--utilities-size-0-375, 6px) var(--utilities-size-0-500, 8px)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500, 8px)',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
    background: 'var(--surface-accent, #5E59F3)',
    boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--surface-hover-accent-hover)',
    },
  },
  utmTableHeaderDetailsCell: {
    display: 'flex',
    width: '60%',
    alignItems: 'center',
    gap: 'var(--utilities-size-1-000, 16px)',
  },
  unitNameCell: {
    display: 'flex',
    flex: '1 0 0',
    width: '332px',
    height: '32px',
    padding:
      'var(--utilities-size-0-250, 4px) var(--utilities-size-0-500, 8px)',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500, 8px)',
    overflow: 'hidden',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
    border: '1px solid var(--border-control-default, rgba(23, 28, 38, 0.20))',
    background: 'var(--surface-default, #FFF)',
    boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
  },
  customFieldNameCell: {
    display: 'flex',
    flex: '1 0 0',
    width: '70px',
    height: '32px',
    padding:
      'var(--utilities-size-0-250, 4px) var(--utilities-size-0-500, 8px)',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500, 8px)',
    overflow: 'hidden',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
    border: '1px solid var(--border-control-default, rgba(23, 28, 38, 0.20))',
    background: 'var(--surface-default, #FFF)',
    boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
  },
  unitNameCellInput: {
    width: '100%',
    color: 'var(--content-primary, #2C313A)',
    fontFamily: 'var(--typography-font-family-default, Inter) !important',
    fontSize: 'var(--typography-font-size-sm, 14px)',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'var(--typography-line-height-sm-line, 20px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
    '&:focus, &:focus-visible': {
      outline: 'unset',
    },
  },
  customFieldCellInput: {
    width: '100%',
    color: 'var(--content-primary, #2C313A)',
    fontFamily: 'var(--typography-font-family-default, Inter) !important',
    fontSize: 'var(--typography-font-size-sm, 14px)',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'var(--typography-line-height-sm-line, 20px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
    '&:focus, &:focus-visible': {
      outline: 'unset',
    },
  },
  checkboxCell: {
    display: 'flex',
    padding: 'var(--utilities-size-0-375, 6px) 0px',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500, 8px)',
    flex: '1 0 0',
  },
  centerCheckboxCell: {
    display: 'flex',
    padding: 'var(--utilities-size-0-375, 6px) 0px',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--utilities-size-0-500, 8px)',
    flex: '1 0 0',
  },
  availabilityCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-250, 4px)',
  },
  dataCell: {
    display: 'flex',
    width: '100px',
    height: '32px',
    padding:
      'var(--utilities-size-0-250, 4px) var(--utilities-size-0-500, 8px)',
    alignItems: 'center',
    gap: 'var(--utilities-size-0-500, 8px)',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
    border: '1px solid var(--border-control-default, rgba(23, 28, 38, 0.20))',
    background: 'var(--surface-default, #FFF)',
    boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
  },
  dataCellText: {
    flex: '1 0 0',
    width: '100%',
    color: 'var(--content-primary, #2C313A)',
    fontFamily: 'var(--typography-font-family-default, Inter) !important',
    fontSize: 'var(--typography-font-size-sm, 14px)',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'var(--typography-line-height-sm-line, 20px)',
    letterSpacing: 'var(--typography-letter-spacing-default, 0px)',
    '&:focus, &:focus-visible': {
      outline: 'unset',
    },
    '&:disabled': {
      background: 'none',
      cursor: 'not-allowed',
      color: 'var(--content-primary-muted, rgba(23, 28, 38, 0.20))',
    },
  },
  checkbox: {
    display: 'flex',
    width: '20px',
    height: '20px',
    padding: 'var(--utilities-size-0-125, 2px)',
    alignItems: 'center',
    borderRadius: 'var(--utilities-border-radius-md, 4px)',
    border: '1px solid var(--border-control-default, rgba(23, 28, 38, 0.20))',
    background: 'var(--surface-default, #FFF)',
    boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
    '& .MuiSvgIcon-root': {
      visibility: 'hidden',
    },
    '&.Mui-checked': {
      display: 'flex',
      padding: 'var(--utilities-size-0-125, 2px)',
      alignItems: 'center',
      borderRadius: 'var(--utilities-border-radius-md, 4px)',
      background: 'var(--surface-accent, #5E59F3)',
      boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
      backgroundImage: 'url(/static/icons/seenic/check.svg)',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'var(--utilities-size-1-000)',
      '&:hover': {
        background: 'var(--surface-accent, #5E59F3)',
        boxShadow: '0px 1px 2px 0px rgba(23, 28, 38, 0.05)',
        backgroundImage: 'url(/static/icons/seenic/check.svg)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'var(--utilities-size-1-000)',
      },
      '& .MuiSvgIcon-root': {
        visibility: 'hidden',
      },
    },
    '&.Mui-disabled': {
      cursor: 'not-allowed',
      pointerEvents: 'unset',
    },
  },
  customFieldName: {
    color: 'var(--content-primary, #2C313A)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-sm)',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 'var(--typography-line-height-sm-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 'inherit',
  },
}));

const useTooltipStyles = makeStyles(() => ({
  tooltip: {
    maxWidth: '260px',
    alignItems: 'center',
    backgroundColor: 'var(--surface-inverted)',
    borderRadius: 'var(--utilities-border-radius-default)',
    boxShadow: 'var(--shadows-popover)',
    display: 'flex',
    gap: 'var(--utilities-size-0-500)',
    padding: 'var(--utilities-size-0-500) var(--utilities-size-0-750)',
    position: 'relative',
    margin: 'var(--utilities-size-0-500)',
    color: 'var(--content-inverted)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-xs)',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 'var(--typography-line-height-xs-line)',
    letterSpacing: 'var(--typography-letter-spacing-default)',
  },
}));

const ctx = document.createElement('canvas').getContext('2d');
const gradient = ctx.createLinearGradient(0, 0, 0, 250);
gradient.addColorStop(0, 'rgba(94, 89, 243, 0.6)');
gradient.addColorStop(1, 'rgba(94, 89, 243, 0)');

const viewDurationGradient = ctx.createLinearGradient(0, 0, 0, 250);
viewDurationGradient.addColorStop(0, 'rgba(94, 89, 243, 0.5)');
viewDurationGradient.addColorStop(1, 'rgba(94, 89, 243, 0.25)');

const viewDurationGradientHover = ctx.createLinearGradient(0, 0, 0, 250);
viewDurationGradientHover.addColorStop(0, 'rgba(94, 89, 243, 1)');
viewDurationGradientHover.addColorStop(1, 'rgba(94, 89, 243, 0.49)');

const Explorer3DOverviewView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  let { listId, companyName, explorer3DName } = useParams();
  listId = decodeURIComponent(listId);
  companyName = decodeURIComponent(companyName);
  explorer3DName = decodeURIComponent(explorer3DName);
  const classes = useStyles();
  const tooltipClass = useTooltipStyles();
  const visitsDateValue = useSelector(state => state.datePicker.range);
  const currentStep = useSelector(state => state.timeStep.value).find(
    ts => ts.isSelected,
  ).name;
  const companyStats = useSelector(state => state.companyStats.data);

  const parentRef = useRef(null);
  const refVisits = useRef();
  const refVisitsScroll = useRef();

  const [width, setWidth] = useState(0);
  const [isEditUnitsMode, setIsEditUnitsMode] = useState(false);
  const [filteredLaunchesStats, setFilteredLaunchesStats] = useState([]);
  const [namedUnits, setNamedUnits] = useState([]);
  const [heatMapStats, setHeatMapStats] = useState(null);
  const [explorerData, setExplorerData] = useState(null);
  const [editUnitsPage, setEditUnitsPage] = useState(0);
  const [downloadIconColor, setDownloadIconColor] = useState(null);
  const [originalUnits, setOriginalUnits] = useState([]);
  const [originalHidePrices, setOriginalHidePrices] = useState(null);
  const [explorerReload, setExplorerReload] = useState(false);

  const stats = useAsync(async () => {
    try {
      const propertyData = companyStats.find(
        c =>
          c.companyName === companyName &&
          decodeURIComponent(c.property_name) === listId,
      );
      const explorer3DLocationId = propertyData.explorers3D.find(
        e => e.name === explorer3DName,
      ).locationId;

      const explorer3DOverallStats = await get3DExplorerOverallStats(
        explorer3DLocationId,
      );

      explorer3DOverallStats.explorer3DLocationId = explorer3DLocationId;
      setIsEditUnitsMode(false);
      setEditUnitsPage(0);
      return explorer3DOverallStats;
    } catch (error) {
      console.error('explorer3D:stats', error);
    }
  }, [companyName, listId, explorer3DName]);

  const explorer3DLaunchesStats = useAsync(async () => {
    try {
      if (!stats.loading) {
        return await get3DExplorerLaunchesChartStats(
          stats.value.explorer3DLocationId,
        );
      }
    } catch (e) {
      console.error('explorer3D:launchesStats', e);
      return [];
    }
  }, [stats.loading]);

  const locationHeatMapStats = useAsync(async () => {
    try {
      if (!stats.loading && !explorer3DLaunchesStats.loading) {
        return await getLocationHeatmapStats(
          stats.value.explorer3DLocationId,
          true,
        );
      }
    } catch (e) {
      console.error('location:heatMapStats', e);
      return [];
    }
  }, [stats.loading]);

  const explorer = useAsync(async () => {
    try {
      if (
        !stats.loading &&
        !explorer3DLaunchesStats.loading &&
        !locationHeatMapStats.loading
      ) {
        const data = await getExplorer3dById(stats.value.explorer3DLocationId);

        const unitsWithDisplayName = data.units.filter(
          unit => unit.displayName !== null && unit.displayName.trim() !== '',
        );

        const unitsWithoutDisplayName = data.units.filter(
          unit => unit.displayName === null || unit.displayName.trim() === '',
        );

        unitsWithDisplayName.sort((a, b) =>
          a.displayName.localeCompare(b.displayName),
        );

        unitsWithoutDisplayName.sort((a, b) => {
          if (a.number === null && b.number === null) return 0;
          if (a.number === null) return 1;
          if (b.number === null) return -1;
          return a.number.localeCompare(b.number);
        });

        setNamedUnits(unitsWithDisplayName);

        data.units = [...unitsWithDisplayName, ...unitsWithoutDisplayName];

        return data;
      }
    } catch (e) {
      console.error('explorer: getData', e);
      return [];
    }
  }, [stats.loading, explorerReload]);

  const handleSavePdf = useCallback(canvas => {
    const imgData = canvas.toDataURL('image/png', 1);
    const docDefinition = {
      content: [
        {
          text: 'Tours Button Clicks Summary',
          style: 'header',
        },
        {
          text: "Information about the user's actions with the viewed tour",
          fontSize: 10,
          margin: [0, 0, 0, 10],
        },
        calculateImgSize(imgData, canvas.width, canvas.height),
      ],
      styles: {
        header: {
          fontSize: 23,
          bold: true,
        },
      },
      pageOrientation: 'landscape',
    };

    pdfMake.createPdf(docDefinition).download();
  }, []);

  const changeVisitsTimestampHandler = useCallback(() => {
    refVisitsScroll.current.scrollLeft = 0;
  }, []);

  useEffect(() => {
    if (refVisitsScroll.current) {
      const { scrollWidth } = refVisitsScroll.current;
      const { clientWidth } = refVisitsScroll.current;

      refVisitsScroll.current.scrollLeft = scrollWidth - clientWidth;
    }
  }, [filteredLaunchesStats]);

  useEffect(() => {
    if (parentRef.current) {
      const handleResize = () => {
        setWidth(parentRef.current.offsetWidth - 422);
      };

      handleResize();

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  useEffect(() => {
    if (
      !explorer3DLaunchesStats.loading &&
      explorer3DLaunchesStats.value &&
      explorer3DLaunchesStats.value[0]
    ) {
      dispatch(
        trySetRange(
          defaultTimeRange(moment(explorer3DLaunchesStats.value[0].eventDate)),
        ),
      );
    }
  }, [dispatch, explorer3DLaunchesStats]);

  useEffect(() => {
    if (
      !explorer3DLaunchesStats.loading &&
      explorer3DLaunchesStats.value &&
      visitsDateValue
    ) {
      setFilteredLaunchesStats(
        filterStatsByDate(
          explorer3DLaunchesStats.value,
          visitsDateValue,
        ).slice(),
      );
    }
  }, [explorer3DLaunchesStats, visitsDateValue]);

  useEffect(() => {
    if (explorer3DLaunchesStats.value != null && visitsDateValue) {
      const dataVisits = calculateEventsInIntervals({
        data: explorer3DLaunchesStats.value
          .slice()
          .map(({ eventDate, eventsCount }) => {
            return {
              eventDate,
              value: eventsCount,
            };
          }),
        range: currentStep,
        start: visitsDateValue[0].format('YYYY-MM-DD'),
        end: visitsDateValue[1].format('YYYY-MM-DD'),
      });

      setFilteredLaunchesStats(
        dataVisits.reverse().map(({ eventDate, value }) => ({
          eventDate,
          eventsCount: value,
        })),
      );
    }
  }, [currentStep, explorer3DLaunchesStats, visitsDateValue]);

  useEffect(() => {
    if (
      !stats.loading &&
      !explorer3DLaunchesStats.loading &&
      locationHeatMapStats.value
    ) {
      setHeatMapStats(locationHeatMapStats.value);
    }
  }, [
    stats.loading,
    explorer3DLaunchesStats.loading,
    locationHeatMapStats.value,
  ]);

  useEffect(() => {
    if (
      !stats.loading &&
      !explorer3DLaunchesStats.loading &&
      !locationHeatMapStats.loading &&
      explorer.value
    ) {
      setExplorerData(explorer.value);
      setOriginalUnits(prevOriginalUnits =>
        prevOriginalUnits.length > 0
          ? prevOriginalUnits
          : cloneDeep(explorer.value.units),
      );
      setOriginalHidePrices(explorer.value.settings.hidePrices);
    }
  }, [
    stats.loading,
    explorer3DLaunchesStats.loading,
    locationHeatMapStats.loading,
    explorer.value,
  ]);

  let locationVisits = {};
  let heatMapData = [];

  if (!stats.loading && !explorer3DLaunchesStats.loading && heatMapStats) {
    heatMapData = heatMapStats;
  }

  if (!stats.loading && !explorer3DLaunchesStats.loading && heatMapStats) {
    locationVisits = heatMapStats.reduce((acc, item) => {
      const subdivisionCode = item.principalSubdivisionCode.split('-')[1];
      const cityKey = `${item.city}, ${subdivisionCode}`;
      if (!acc[cityKey]) {
        acc[cityKey] = 0;
      }
      acc[cityKey] += item.visitsCount;
      return acc;
    }, {});
  }

  const sortedLocations = Object.entries(locationVisits).sort(
    (a, b) => b[1] - a[1],
  );

  const [page, setPage] = useState(0);
  const [hoveredArrowLeft, setHoveredArrowLeft] = useState(null);
  const [hoveredArrowRight, setHoveredArrowRight] = useState(null);
  const itemsPerPage = 10;
  const editableItemsPerPage = 10;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeEditUnitsPage = (event, newPage) => {
    setEditUnitsPage(newPage);
  };

  const handleOpenUnitsChange = () => {
    setIsEditUnitsMode(true);
  };

  const handleSaveUnitsChange = useCallback(async () => {
    setIsEditUnitsMode(false);
    try {
      if (!stats.loading && !explorer.loading) {
        const explorer3dId = explorerData.id;
        const changedUnits = explorerData.units.filter((unit, index) => {
          return JSON.stringify(unit) !== JSON.stringify(originalUnits[index]);
        });
        const changedHidePrices = explorerData.settings.hidePrices;

        if (
          changedUnits.length > 0 ||
          changedHidePrices !== originalHidePrices
        ) {
          await Promise.all([
            updateBulkExplorer3DUnits({
              explorer3dId,
              units: changedUnits,
              hidePrices: changedHidePrices,
            }),
          ]);
          setOriginalHidePrices(changedHidePrices);
          setExplorerReload(prevExplorerReload => !prevExplorerReload);
          toast.success('Units successfully updated!', {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 3000,
          });
        } else {
          console.log('No changes in units');
          toast.info('No changes in units to update.', {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 3000,
          });
        }
      }
    } catch (error) {
      console.error('Error updating units settings:', error);
      toast.error('Failed to update units. Please try again.', {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 3000,
      });
    }
  }, [
    stats.loading,
    explorer.loading,
    explorerData,
    originalUnits,
    originalHidePrices,
  ]);

  const getUnitDisplayName = useCallback(
    unitTitle => {
      if (!unitTitle) return null;

      const [numberPart] = unitTitle.match(/\d+/);
      if (!numberPart) return null;

      const foundUnit = namedUnits.find(
        unit => String(unit.number) === numberPart,
      );

      return foundUnit?.displayName ?? unitTitle;
    },
    [namedUnits],
  );

  const handleCloseUnitsChange = () => {
    setIsEditUnitsMode(false);
  };

  function refactorStats(data) {
    const result = { ...data };

    if (result.views_count) {
      result.total_views_count =
        result.views_count.location_3d_explorer_total_views || 0;
      result.mobile_views =
        result.views_count.location_3d_explorer_views_mobile || 0;
      result.views_count_with_duration_less_than_one_minute =
        result.views_count
          .location_3d_explorer_views_duration_less_than_one_minute || 0;
      result.bonus_rate =
        (result.views_count_with_duration_less_than_one_minute /
          result.total_views_count) *
          100 || 0;
    }

    if (result.last_month_views_count) {
      result.total_last_month_views_count =
        result.last_month_views_count
          .last_month_location_3d_explorer_total_views || 0;
    }

    if (result.duration) {
      result.total_duration =
        result.duration.location_3d_explorer_views_duration || 0;
    }

    if (result.conversion) {
      result.total_conversion = result.conversion.submit || 0;
    }

    if (result.users_engagement) {
      result.overview_3d_total_views =
        result.users_engagement.overview_3d_total_views || 0;
      result.apartment_card_total_views =
        result.users_engagement.apartment_card_total_views || 0;
      result.apartments_on_plan_total_views =
        result.users_engagement.apartments_on_plan_total_views || 0;
      result.neighborhood_total_views =
        result.users_engagement.neighborhood_total_views || 0;
      result.amenities_total_views =
        result.users_engagement.amenities_total_views || 0;
    }

    if (result.amenities_views_stats) {
      result.amenities_views = result.amenities_views_stats.sort(
        (a, b) => b.views_count - a.views_count,
      );
    }

    if (result.units_views_stats) {
      result.units = result.units_views_stats
        .sort((a, b) => b.views_count - a.views_count)
        .map(unit => {
          const conversion = result.units_conversion_stats.find(
            conversionUnit => conversionUnit.unit_title === unit.unit_title,
          );

          return {
            ...unit,
            submit_conversion: conversion ? conversion.submit_conversion : 0,
          };
        });
    }

    return result;
  }

  const explorer3DOverallStats = useMemo(() => {
    if (stats.loading) return {};
    return refactorStats(stats.value);
  }, [stats]);

  const paginatedUnits = useMemo(() => {
    const startIndex = page * itemsPerPage;
    return (
      !stats.loading &&
      explorer3DOverallStats.units.slice(startIndex, startIndex + itemsPerPage)
    );
  }, [stats, page]);

  return (
    <Page className={classes.root} title="Overview">
      <Header>
        <Box className={classes.headerContainer}>
          <Box className={classes.headerTextSection}>
            <Box className={classes.backButton} onClick={() => navigate(-1)}>
              <ArrowLeft className={classes.backButtonIcon} />
            </Box>
            <Box className={classes.headerTitleContainer}>
              <Box className={classes.headerTitle}>
                <Box className={classes.headerTitleText}>
                  <Link to={`/overview/multi/${companyName}/${listId}`}>
                    {listId}
                  </Link>
                </Box>
                <LeadIcon2 className={classes.leadIcon2} />
                <Box
                  className={classes.headerTitleText}
                  style={{ color: 'var(--content-primary)' }}
                >
                  {explorer3DName}
                </Box>
              </Box>
              <Box className={classes.headerSubTitleText}>
                3D Explorer Summary
              </Box>
            </Box>
          </Box>
          <Box className={classes.headerButtonsSection}>
            <a
              href={`${process.env.EXPLORER3D_URL}/overview?explorer3dId=${explorer3DOverallStats.explorer3DLocationId}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              <Box className={classes.explorerButton}>
                <Cube
                  className={classes.buttonIcon}
                  color={'var(--content-inverted)'}
                />
                <Box className={classes.explorerButtonText}>3D Explorer</Box>
              </Box>
            </a>
          </Box>
        </Box>
      </Header>

      <Box className={classes.infoCardContainer}>
        <Box className={classes.infoCardsRow}>
          <CardStat
            className={classes.cardStat}
            frameClassName={classes.designComponentInstanceNode}
            icon="off"
            title="Total Launches"
            loading={stats.loading}
            value={
              explorer3DOverallStats.total_views_count !== 0
                ? explorer3DOverallStats.total_views_count
                : -1
            }
          />
          <CardStat
            className={classes.cardStat}
            frameClassName={classes.designComponentInstanceNode}
            icon="off"
            title="Last Month Launches"
            loading={stats.loading}
            value={
              explorer3DOverallStats.total_last_month_views_count !== 0
                ? explorer3DOverallStats.total_last_month_views_count
                : -1
            }
          />
          <CardStat
            className={classes.cardStat}
            frameClassName={classes.designComponentInstanceNode}
            icon="off"
            title="Total View Duration"
            loading={stats.loading}
            value={
              explorer3DOverallStats.total_duration !== 0
                ? formatDateFromSeconds(explorer3DOverallStats.total_duration)
                : '00:00'
            }
          />
          <CardStat
            className={classes.cardStat}
            frameClassName={classes.designComponentInstanceNode}
            icon="off"
            title="Avg. View Duration"
            tooltipText="Average Views Duration"
            loading={stats.loading}
            value={
              explorer3DOverallStats.average_views_duration !== 0 &&
              explorer3DOverallStats.average_views_duration > 1
                ? formatDateFromSeconds(
                    explorer3DOverallStats.average_views_duration,
                  )
                : '00:00'
            }
          />
        </Box>
      </Box>
      <Box className={classes.infoCardContainer} style={{ paddingTop: '8px' }}>
        <Box className={classes.infoCardsRow}>
          <CardStat
            className={classes.cardStat}
            frameClassName={classes.designComponentInstanceNode}
            icon="off"
            title="Submit Conversion"
            loading={stats.loading}
            value={
              explorer3DOverallStats.total_conversion !== 0
                ? explorer3DOverallStats.total_conversion
                : -1
            }
            tooltipText="Amounts of clicks to Submit button"
          />
          <CardStat
            className={classes.cardStat}
            frameClassName={classes.designComponentInstanceNode}
            icon="off"
            title="Bounce Rate"
            loading={stats.loading}
            complexTooltip
            tooltipText={`Amounts of visits with duration less than 1 minute: ${explorer3DOverallStats.views_count_with_duration_less_than_one_minute}`}
            value={`${roundToTwoDecimalPlaces(
              explorer3DOverallStats.bonus_rate,
            )}%`}
          />
          <CardStat
            className={classes.cardStat}
            frameClassName={classes.designComponentInstanceNode}
            loading={stats.loading}
            icon="on"
            title="Mobile"
            complexTooltip
            tooltipText={`Total Mobile Launches: ${
              stats.value && explorer3DOverallStats.mobile_views
            }`}
            value={
              (explorer3DOverallStats.mobile_views /
                explorer3DOverallStats.total_views_count) *
              100
            }
          />
          <CardStat
            className={classes.cardStat}
            frameClassName={classes.designComponentInstanceNode}
            loading={stats.loading}
            icon="on"
            title="Desktop"
            complexTooltip
            tooltipText={`Total Desktop Launches: ${
              stats.value &&
              explorer3DOverallStats.total_views_count -
                explorer3DOverallStats.mobile_views
            }`}
            value={
              100 -
              (explorer3DOverallStats.mobile_views /
                explorer3DOverallStats.total_views_count) *
                100
            }
          />
        </Box>
      </Box>

      <Box
        className="w-full flex gap-2 mt-2 mb-2"
        style={{ borderRadius: '8px', height: '350px' }}
      >
        <Box
          className={`w-full h-auto bg-white relative ${classes.utmContainer}`}
        >
          <Box className={classes.sectionHeader}>Users Engagement</Box>
          <Box ref={parentRef} className={classes.sectionContent}>
            <Box className={classes.sectionCard}>
              <Box className={classes.sectionIconBox}>
                <Cube
                  className={classes.sectionIcon}
                  color={'var(--surface-default)'}
                />
                <ArrowRight
                  className={classes.secondSectionIconBox}
                  color={'var(--surface-default)'}
                />
              </Box>
              <Box className={classes.sectionBox}>
                <Box className={classes.sectionBoxContent}>
                  <Box className={classes.sectionBoxContentTitle}>
                    3D Overview
                  </Box>
                  <Box className={classes.sectionBoxContentBodyCount}>
                    {stats.loading ? (
                      <Skeleton height="100%" />
                    ) : (
                      explorer3DOverallStats.overview_3d_total_views
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              className={classes.connection}
              style={{ width: `${width}px` }}
            />
            <Box
              className={classes.secondConnection}
              style={{ width: `${width}px` }}
            />
            <Box
              className={classes.sectionCard}
              style={{
                backgroundColor: 'var(--palette-gray-600)',
                marginTop: '60px',
                zIndex: '1',
              }}
            >
              <Box className={classes.sectionIconBox}>
                <Squares
                  className={classes.sectionIcon}
                  color={'var(--surface-default)'}
                />
                <ArrowRight
                  className={classes.secondSectionIconBox}
                  color={'var(--surface-default)'}
                />
              </Box>
              <Box
                className={classes.sectionBox}
                style={{
                  backgroundColor: 'var(--palette-gray-100)',
                  borderColor: 'var(--palette-gray-600)',
                }}
              >
                <Box className={classes.sectionBoxContent}>
                  <Box
                    className={classes.sectionBoxContentTitle}
                    style={{ color: 'var(--palette-gray-600)' }}
                  >
                    Sitemap
                  </Box>
                  <Box
                    className={classes.sectionBoxContentBodyCount}
                    style={{ color: 'var(--palette-gray-600)' }}
                  >
                    {stats.loading ? (
                      <Skeleton height="100%" />
                    ) : (
                      explorer3DOverallStats.apartments_on_plan_total_views
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              className={classes.sectionCard}
              style={{
                backgroundColor: 'var(--palette-gray-600)',
                marginTop: '60px',
                zIndex: '1',
              }}
            >
              <Box className={classes.sectionIconBox}>
                <Lotus
                  className={classes.sectionIcon}
                  color={'var(--surface-default)'}
                />
                <ArrowRight
                  className={classes.secondSectionIconBox}
                  color={'var(--surface-default)'}
                />
              </Box>
              <Box
                className={classes.sectionBox}
                style={{
                  backgroundColor: 'var(--palette-gray-100)',
                  borderColor: 'var(--palette-gray-600)',
                }}
              >
                <Box className={classes.sectionBoxContent}>
                  <Box
                    className={classes.sectionBoxContentTitle}
                    style={{ color: 'var(--palette-gray-600)' }}
                  >
                    Amenities
                  </Box>
                  <Box
                    className={classes.sectionBoxContentBodyCount}
                    style={{ color: 'var(--palette-gray-600)' }}
                  >
                    {stats.loading ? (
                      <Skeleton height="100%" />
                    ) : (
                      explorer3DOverallStats.amenities_total_views
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              className={classes.sectionCard}
              style={{
                backgroundColor: 'var(--palette-gray-600)',
                marginTop: '60px',
                zIndex: '1',
              }}
            >
              <Box className={classes.sectionIconBox}>
                <Map
                  className={classes.sectionIcon}
                  color={'var(--surface-default)'}
                />
                <ArrowRight
                  className={classes.secondSectionIconBox}
                  color={'var(--surface-default)'}
                />
              </Box>
              <Box
                className={classes.sectionBox}
                style={{
                  backgroundColor: 'var(--palette-gray-100)',
                  borderColor: 'var(--palette-gray-600)',
                }}
              >
                <Box className={classes.sectionBoxContent}>
                  <Box
                    className={classes.sectionBoxContentTitle}
                    style={{ color: 'var(--palette-gray-600)' }}
                  >
                    Neighborhood
                  </Box>
                  <Box
                    className={classes.sectionBoxContentBodyCount}
                    style={{ color: 'var(--palette-gray-600)' }}
                  >
                    {stats.loading ? (
                      <Skeleton height="100%" />
                    ) : (
                      explorer3DOverallStats.neighborhood_total_views
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              className={classes.sectionCard}
              style={{ backgroundColor: 'var(--palette-green-600)' }}
            >
              <Box className={classes.sectionIconBox}>
                <Build
                  className={classes.sectionIcon}
                  color={'var(--surface-default)'}
                />
                <Check
                  className={classes.secondSectionIconBox}
                  color={'var(--surface-default)'}
                />
              </Box>
              <Box
                className={classes.sectionBox}
                style={{
                  backgroundColor: 'var(--palette-green-100)',
                  borderColor: 'var(--palette-green-600)',
                }}
              >
                <Box className={classes.sectionBoxContent}>
                  <Box
                    className={classes.sectionBoxContentTitle}
                    style={{ color: 'var(--palette-green-600)' }}
                  >
                    Apartment Card
                  </Box>
                  <Box
                    className={classes.sectionBoxContentBodyCount}
                    style={{ color: 'var(--palette-green-600)' }}
                  >
                    {stats.loading ? (
                      <Skeleton height="100%" />
                    ) : (
                      explorer3DOverallStats.apartment_card_total_views
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        className="w-full flex gap-2 mt-2 mb-2"
        style={{ borderRadius: '8px', minHeight: '273px' }}
      >
        <Box
          className="w-3/5 h-auto bg-white relative"
          style={{
            padding: '0 20px 10px',
            borderRadius: '8px',
            boxShadow: 'var(--shadows-subtle)',
          }}
        >
          <Box className={classes.chartHeaderContainer}>
            <Box className="flex">
              <Tooltip
                placement="top"
                title="Amount of launches applied range of times"
                classes={tooltipClass}
              >
                <Box className={classes.sectionHeader}>Launches Summary</Box>
              </Tooltip>
              <Box
                className={classes.downloadIconContainer}
                onMouseEnter={() => setDownloadIconColor('5E59F3')}
                onMouseLeave={() => setDownloadIconColor(null)}
                onClick={() =>
                  handleSavePdf(refVisits.current.chartInstance.canvas)
                }
              >
                <Download
                  className={classes.downloadIcon}
                  color={downloadIconColor || '2C313A'}
                />
              </Box>
              <Box className="flex">
                <TimeStep
                  visitsDateValue={visitsDateValue}
                  onClick={changeVisitsTimestampHandler}
                />
              </Box>
            </Box>
            <Box
              style={{
                display: 'flex',
                flexDirection: 'column',
                rowGap: '1rem',
              }}
            />
            <MultiDateRangePicker />
          </Box>
          <Box
            className={`w-full relative flex flex-col ${classes.container}`}
            ref={refVisitsScroll}
            style={{
              overflow: 'auto',
            }}
          >
            <Box
              style={{
                position: 'relative',
                height: '100%',
                minWidth:
                  filteredLaunchesStats.length *
                  calculateChartSize(currentStep),
                overflowY: 'overlay',
              }}
            >
              {filteredLaunchesStats.length !== 0 ? (
                <Line
                  ref={refVisits}
                  data={{
                    datasets: [
                      {
                        backgroundColor: gradient,
                        lineTension: 0.01,
                        borderColor: '#5E59F3',
                        pointBackgroundColor: 'transparent',
                        pointBorderColor: 'transparent',
                        pointRadius: size(15),
                        pointHoverRadius: size(15),
                        borderWidth: 1,
                        parsing: false,
                        data: filteredLaunchesStats.map(
                          item => item.eventsCount,
                        ),
                        label: '',
                      },
                    ],
                    labels: formatLabels(filteredLaunchesStats, currentStep),
                  }}
                  options={{
                    normalized: true,
                    animation: false,
                    cornerRadius: size(20),
                    layout: {
                      padding: {
                        right: size(13),
                      },
                    },
                    legend: { display: false },
                    maintainAspectRatio: false,
                    responsive: true,
                    devicePixelRatio: window.devicePixelRatio * 2,

                    hover: {
                      onHover(e, [chart]) {
                        if (!chart) {
                          dispatch(
                            updateVisitsTooltipInfo({
                              show: false,
                              showDashes: false,
                              showLabel: false,
                              showCircle: false,
                            }),
                          );
                          return;
                        }

                        const value = filteredLaunchesStats[chart._index];

                        dispatch(
                          setVisitsTooltipInfo({
                            showCircle: true,
                            showDashes: true,
                            type: TOOLTIP_TYPE.SMALL,
                            show: true,
                            value,
                            label: chart._xScale.ticks[chart._index],
                            smallTooltipText: `${value.eventsCount}`,
                            dashesHeight: `${
                              refVisits.current.chartInstance.height - 20
                            }px`,
                            showLabel: true,
                            tooltipLeft:
                              chart._view.x -
                              e.target.parentElement.parentElement.scrollLeft +
                              20,
                            tooltipTop: chart._view.y + 28,
                            circleLeft:
                              chart._view.x -
                              e.target.parentElement.parentElement.scrollLeft +
                              10,
                            circleTop: chart._view.y + 50,
                            dashesLeft:
                              chart._view.x -
                              e.target.parentElement.parentElement.scrollLeft +
                              20,
                            dashesTop:
                              e.target.parentElement.parentElement.offsetTop,
                            labelLeft:
                              chart._view.x -
                              e.target.parentElement.parentElement.scrollLeft +
                              20,
                            labelBottom: 15,
                          }),
                        );
                      },
                    },
                    tooltips: {
                      enabled: false,
                      intersect: false,
                    },
                    scales: {
                      xAxes: [
                        {
                          ticks: {
                            fontSize: size(8),
                            reverse: true,
                          },
                          categoryPercentage: 0.97,
                          barPercentage: 0.97,
                          barThickness: 28,
                          scaleLabel: {
                            display: true,
                            labelString: 'Date',
                          },
                        },
                      ],
                      yAxes: [
                        {
                          ticks: {
                            fontStyle: 'bold',
                            fontColor: '#000',
                            stepSize: 250,
                            beginAtZero: true,
                          },
                          scaleLabel: {
                            display: true,
                            labelString: 'Views',
                          },
                        },
                      ],
                    },
                  }}
                />
              ) : (
                <Skeleton height="99%" />
              )}
            </Box>
          </Box>
          <GraphTooltip store="visitsTooltip" />
        </Box>
        <Box
          className={`w-2/5 h-auto bg-white relative ${classes.utmContainer}`}
          style={{ maxHeight: '442px', overflowY: 'auto' }}
        >
          <Box className={classes.sectionHeader}>Amenities Views</Box>
          <Box className={classes.utmTable}>
            <Box className={classes.utmTableRow}>
              <Box
                style={{ flex: '1 0 0' }}
                className={classes.utmTableHeaderCell}
              >
                Amenities
              </Box>
              <Box
                style={{
                  width: '80px',
                  textAlign: 'right',
                }}
                className={classes.utmTableHeaderCell}
              >
                Total views
              </Box>
            </Box>
            {!stats.loading &&
            explorer3DOverallStats.amenities_views.length > 0 ? (
              explorer3DOverallStats.amenities_views.map(item => (
                <Box
                  style={{ borderBottom: '1px solid var(--border-default)' }}
                  className={classes.utmTableRow}
                  key={`${item.amenity} + ${item.views_count}`}
                >
                  <Box
                    style={{ flex: '1 0 0', color: 'var(--content-primary)' }}
                    className={classes.utmTableHeaderCell}
                  >
                    {item.amenity}
                  </Box>
                  <Box
                    style={{
                      width: '80px',
                      textAlign: 'right',
                      color: 'var(--content-primary)',
                    }}
                    className={classes.utmTableHeaderCell}
                  >
                    {item.views_count}
                  </Box>
                </Box>
              ))
            ) : (
              <Box className={classes.emptyHeatMapTable}>Nothing found</Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        className="w-full flex gap-2 mt-2 mb-2"
        style={{ borderRadius: '8px', minHeight: '273px' }}
      >
        <Box
          className={`w-full h-auto bg-white relative ${classes.heatMapContainer}`}
        >
          <Box className={classes.sectionHeader}>User Locations</Box>
          <Box className={classes.heatMapContent}>
            <Box className={classes.heatMapTable}>
              <Box className={classes.utmTableRow}>
                <Box
                  style={{ flex: '1 0 0' }}
                  className={classes.utmTableHeaderCell}
                >
                  Region
                </Box>
                <Box
                  style={{
                    width: 'var(--utilities-size-5-000)',
                    textAlign: 'right',
                  }}
                  className={classes.utmTableHeaderCell}
                >
                  Total visits
                </Box>
              </Box>
              {sortedLocations.length > 0 ? (
                sortedLocations.map(([city, count]) => (
                  <Box
                    key={city}
                    style={{ borderBottom: '1px solid var(--border-default)' }}
                    className={classes.utmTableRow}
                  >
                    <Box
                      style={{ flex: '1 0 0', color: 'var(--content-primary)' }}
                      className={classes.utmTableHeaderCell}
                    >
                      {city}
                    </Box>
                    <Box
                      style={{
                        width: 'var(--utilities-size-5-000)',
                        textAlign: 'right',
                        color: 'var(--content-primary)',
                      }}
                      className={classes.utmTableHeaderCell}
                    >
                      {count}
                    </Box>
                  </Box>
                ))
              ) : (
                <Box className={classes.emptyHeatMapTable}>Nothing found</Box>
              )}
            </Box>
            {heatMapData && <HeatMap data={heatMapData} />}
          </Box>
        </Box>
      </Box>

      <Box
        className="w-full flex gap-2 mt-2 mb-2"
        style={{ borderRadius: '8px', minHeight: '273px' }}
      >
        <Box
          className={`w-full h-auto bg-white relative ${classes.utmContainer}`}
        >
          <Box className={classes.unitsSection}>
            <Box className={classes.sectionHeader} style={{ flex: '1 0 0' }}>
              Units Summary
            </Box>
            {isEditUnitsMode ? (
              <>
                {explorerData?.settings && (
                  <Box className={classes.hidePricesSetting}>
                    <Checkbox
                      disableRipple
                      className={classes.checkbox}
                      checked={Boolean(explorerData.settings.hidePrices)}
                      onChange={e => {
                        setExplorerData({
                          ...explorerData,
                          settings: {
                            ...explorerData.settings,
                            hidePrices: e.target.checked,
                          },
                        });
                      }}
                    />
                    <Box className={classes.hidePricesSettingLabel}>
                      Hide all prices
                    </Box>
                  </Box>
                )}
                <Box
                  className={classes.saveButton}
                  onClick={handleSaveUnitsChange}
                >
                  <Check2
                    className={classes.editButtonIcon}
                    color={'var(--content-inverted)'}
                  />
                  <Box className={classes.whiteButtonText}>Save changes</Box>
                </Box>
                <Box
                  className={classes.closeButton}
                  onClick={handleCloseUnitsChange}
                >
                  <Box className={classes.closeButtonText}>Close</Box>
                </Box>
              </>
            ) : (
              <Box
                className={classes.manageButton}
                onClick={handleOpenUnitsChange}
              >
                <Edit
                  className={classes.editButtonIcon}
                  color={'var(--content-inverted)'}
                />
                <Box className={classes.whiteButtonText}>Manage units</Box>
              </Box>
            )}
          </Box>
          {isEditUnitsMode && !explorer.loading ? (
            <Box className={classes.utmTable}>
              <Box className={classes.utmTableRow}>
                <Box
                  style={{ flex: '1 0 0', width: '115px' }}
                  className={classes.utmTableHeaderCell}
                >
                  Display name
                </Box>
                <Box className={classes.utmTableHeaderDetailsCell}>
                  <Box
                    style={{ flex: '1 0 0', width: '115px' }}
                    className={classes.utmTableHeaderCell}
                  >
                    Balcony tab name
                  </Box>
                  <Box
                    style={{
                      flex: '1 0 0',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    className={classes.utmTableHeaderCell}
                  >
                    {/* {unit.customField1Name || */}
                    {/*   'Custom Field 1'} */}
                  </Box>
                  <Box
                    style={{ flex: '1 0 0' }}
                    className={classes.utmTableHeaderCell}
                  >
                    {/* {unit.customField2Name || */}
                    {/*   'Custom Field 2'} */}
                  </Box>
                  <Box
                    style={{ flex: '1 0 0' }}
                    className={classes.utmTableHeaderCell}
                  >
                    {/* {unit.customField3Name || */}
                    {/*   'Custom Field 3'} */}
                  </Box>
                  <Box
                    style={{ flex: '1 0 0' }}
                    className={classes.utmTableHeaderCell}
                  >
                    {/* {unit.customField4Name || */}
                    {/*   'Custom Field 4'} */}
                  </Box>
                  <Box
                    style={{ flex: '1 0 0', textAlign: 'center' }}
                    className={classes.utmTableHeaderCell}
                  >
                    {'Hide Price'}
                  </Box>
                </Box>
                <Box
                  style={{
                    width: '144px',
                  }}
                  className={classes.utmTableHeaderCell}
                >
                  Availability
                </Box>
                <Box
                  style={{
                    width: '100px',
                    textAlign: 'right',
                  }}
                  className={classes.utmTableHeaderCell}
                >
                  Price
                </Box>
              </Box>
              {!stats.loading && explorerData.units.length > 0 ? (
                explorerData.units
                  .slice(
                    editUnitsPage * editableItemsPerPage,
                    editUnitsPage * editableItemsPerPage + editableItemsPerPage,
                  )
                  .map((unit, localIndex) => {
                    const globalIndex =
                      editUnitsPage * editableItemsPerPage + localIndex;

                    return (
                      <Box
                        key={unit.id}
                        className={classes.utmTableRow}
                        style={{ borderBottom: 0 }}
                      >
                        <Box className={classes.unitNameCell}>
                          <input
                            name="text"
                            placeholder={`${unit.number} Apartment`}
                            className={classes.unitNameCellInput}
                            onChange={e => {
                              const updatedUnits = [...explorerData.units];
                              updatedUnits[globalIndex].displayName =
                                e.target.value || null;
                              setExplorerData({
                                ...explorerData,
                                units: updatedUnits,
                              });
                            }}
                            value={unit.displayName}
                          />
                        </Box>
                        <Box className={classes.utmTableHeaderDetailsCell}>
                          <Box className={classes.unitNameCell}>
                            <input
                              name="text"
                              placeholder={`Balcony tab name`}
                              className={classes.unitNameCellInput}
                              onChange={e => {
                                const updatedUnits = [...explorerData.units];
                                updatedUnits[globalIndex].balconyTabName =
                                  e.target.value || null;
                                setExplorerData({
                                  ...explorerData,
                                  units: updatedUnits,
                                });
                              }}
                              value={unit.balconyTabName}
                            />
                          </Box>
                          <Box className={classes.checkboxCell}>
                            <Box className={classes.customFieldNameCell}>
                              <Box className={classes.customFieldName}>
                                <input
                                  name="text"
                                  placeholder={`Custom Field 1`}
                                  className={classes.customFieldCellInput}
                                  onChange={e => {
                                    const updatedUnits = [
                                      ...explorerData.units,
                                    ];
                                    updatedUnits[globalIndex].customField1Name =
                                      e.target.value || '';
                                    setExplorerData({
                                      ...explorerData,
                                      units: updatedUnits,
                                    });
                                  }}
                                  value={unit.customField1Name}
                                />
                              </Box>
                            </Box>
                            <Checkbox
                              disableRipple
                              className={classes.checkbox}
                              checked={unit.info.customField1}
                              onChange={e => {
                                const updatedUnits = [...explorerData.units];
                                updatedUnits[globalIndex].info.customField1 =
                                  e.target.checked;
                                setExplorerData({
                                  ...explorerData,
                                  units: updatedUnits,
                                });
                              }}
                            />
                          </Box>
                          <Box className={classes.checkboxCell}>
                            <Box className={classes.customFieldNameCell}>
                              <Box className={classes.customFieldName}>
                                <input
                                  name="text"
                                  placeholder={`Custom Field 2`}
                                  className={classes.customFieldCellInput}
                                  onChange={e => {
                                    const updatedUnits = [
                                      ...explorerData.units,
                                    ];
                                    updatedUnits[globalIndex].customField2Name =
                                      e.target.value || '';
                                    setExplorerData({
                                      ...explorerData,
                                      units: updatedUnits,
                                    });
                                  }}
                                  value={unit.customField2Name}
                                />
                              </Box>
                            </Box>
                            <Checkbox
                              disableRipple
                              className={classes.checkbox}
                              checked={unit.info.customField2}
                              onChange={e => {
                                const updatedUnits = [...explorerData.units];
                                updatedUnits[globalIndex].info.customField2 =
                                  e.target.checked;
                                setExplorerData({
                                  ...explorerData,
                                  units: updatedUnits,
                                });
                              }}
                            />
                          </Box>
                          <Box className={classes.checkboxCell}>
                            <Box className={classes.customFieldNameCell}>
                              <Box className={classes.customFieldName}>
                                <input
                                  name="text"
                                  placeholder={`Custom Field 3`}
                                  className={classes.customFieldCellInput}
                                  onChange={e => {
                                    const updatedUnits = [
                                      ...explorerData.units,
                                    ];
                                    updatedUnits[globalIndex].customField3Name =
                                      e.target.value || '';
                                    setExplorerData({
                                      ...explorerData,
                                      units: updatedUnits,
                                    });
                                  }}
                                  value={unit.customField3Name}
                                />
                              </Box>
                            </Box>
                            <Checkbox
                              disableRipple
                              className={classes.checkbox}
                              checked={unit.info.customField3}
                              onChange={e => {
                                const updatedUnits = [...explorerData.units];
                                updatedUnits[globalIndex].info.customField3 =
                                  e.target.checked;
                                setExplorerData({
                                  ...explorerData,
                                  units: updatedUnits,
                                });
                              }}
                            />
                          </Box>
                          <Box className={classes.checkboxCell}>
                            <Box className={classes.customFieldNameCell}>
                              <Box className={classes.customFieldName}>
                                <input
                                  name="text"
                                  placeholder={`Custom Field 4`}
                                  className={classes.customFieldCellInput}
                                  onChange={e => {
                                    const updatedUnits = [
                                      ...explorerData.units,
                                    ];
                                    updatedUnits[globalIndex].customField4Name =
                                      e.target.value || '';
                                    setExplorerData({
                                      ...explorerData,
                                      units: updatedUnits,
                                    });
                                  }}
                                  value={unit.customField4Name}
                                />
                              </Box>
                            </Box>
                            <Checkbox
                              disableRipple
                              className={classes.checkbox}
                              checked={unit.info.customField4}
                              onChange={e => {
                                const updatedUnits = [...explorerData.units];
                                updatedUnits[globalIndex].info.customField4 =
                                  e.target.checked;
                                setExplorerData({
                                  ...explorerData,
                                  units: updatedUnits,
                                });
                              }}
                            />
                          </Box>
                          <Box className={classes.centerCheckboxCell}>
                            <Checkbox
                              disableRipple
                              className={classes.checkbox}
                              checked={Boolean(unit.hidePrice)}
                              onChange={e => {
                                const updatedUnits = [...explorerData.units];
                                updatedUnits[globalIndex] = {
                                  ...updatedUnits[globalIndex],
                                  hidePrice: e.target.checked,
                                };
                                setExplorerData({
                                  ...explorerData,
                                  units: updatedUnits,
                                });
                              }}
                            />
                          </Box>
                        </Box>
                        <Box className={classes.availabilityCell}>
                          <Checkbox
                            disableRipple
                            className={classes.checkbox}
                            checked={!unit.busy}
                            disabled={explorerData.settings.source !== 'manual'}
                            onChange={e => {
                              const updatedUnits = explorerData.units.map(
                                (unit, i) =>
                                  i === globalIndex
                                    ? { ...unit, busy: !e.target.checked }
                                    : unit,
                              );
                              setExplorerData({
                                ...explorerData,
                                units: updatedUnits,
                              });
                            }}
                          />
                          <ChevronRight />
                          <Box
                            className={classes.dataCell}
                            style={{
                              background:
                                explorerData.settings.source !== 'manual' &&
                                'var(--surface-tint-medium, rgba(23, 28, 38, 0.10))',
                              borderColor:
                                explorerData.settings.source !== 'manual' &&
                                'var(--surface-tint-medium, rgba(23, 28, 38, 0.10))',
                            }}
                          >
                            <input
                              name="text"
                              placeholder=""
                              className={classes.dataCellText}
                              style={{
                                color:
                                  explorerData.settings.source !== 'manual' &&
                                  'var(--content-primary-muted, rgba(23, 28, 38, 0.20))',
                              }}
                              onChange={e => {
                                const updatedUnits = [...explorerData.units];
                                updatedUnits[globalIndex].availableAt =
                                  e.target.value;
                                setExplorerData({
                                  ...explorerData,
                                  units: updatedUnits,
                                });
                              }}
                              value={dateConverterForUnits(
                                unit.availableAt,
                                explorerData.settings.dateFormat,
                              )}
                              disabled={
                                explorerData.settings.source !== 'manual'
                              }
                            />
                          </Box>
                        </Box>
                        <Box
                          className={classes.dataCell}
                          style={{ width: '100px' }}
                        >
                          <Box
                            className={classes.dataCellText}
                            style={{
                              maxWidth: '20%',
                              color:
                                'var(--content-primary-weak, rgba(23, 28, 38, 0.40))',
                            }}
                          >
                            {getCurrencySymbol(explorerData.settings.currency)}
                          </Box>
                          <input
                            name="text"
                            placeholder=""
                            className={classes.dataCellText}
                            style={{
                              textAlign: 'right',
                              color:
                                explorerData.settings.source !== 'manual' &&
                                'var(--content-primary, #2C313A)',
                            }}
                            onChange={e => {
                              const value = e.target.value.replace(/,/g, '');
                              const updatedUnits = [...explorerData.units];
                              updatedUnits[globalIndex].price =
                                isNaN(value) || value === ''
                                  ? ''
                                  : Number(value);
                              setExplorerData({
                                ...explorerData,
                                units: updatedUnits,
                              });
                            }}
                            value={
                              unit.price !== '' && !isNaN(unit.price)
                                ? formatNumberWithCommas(unit.price)
                                : unit.price
                            }
                            disabled={explorerData.settings.source !== 'manual'}
                          />
                        </Box>
                      </Box>
                    );
                  })
              ) : (
                <Box className={classes.emptyHeatMapTable}>Nothing found</Box>
              )}
            </Box>
          ) : (
            <Box className={classes.utmTable}>
              <Box className={classes.utmTableRow}>
                <Box
                  style={{ flex: '1 0 0' }}
                  className={classes.utmTableHeaderCell}
                >
                  Unit Title
                </Box>
                <Box
                  style={{
                    width: '80px',
                    textAlign: 'right',
                  }}
                  className={classes.utmTableHeaderCell}
                >
                  Total views
                </Box>
                <Box
                  style={{
                    width: '140px',
                    textAlign: 'right',
                  }}
                  className={classes.utmTableHeaderCell}
                >
                  Conversion
                </Box>
              </Box>
              {!stats.loading && explorer3DOverallStats.units.length > 0 ? (
                paginatedUnits.map(item => (
                  <Box
                    style={{ borderBottom: '1px solid var(--border-default)' }}
                    className={classes.utmTableRow}
                    key={`${item.unit_title} + ${item.views_count}`}
                  >
                    {explorer.loading ? (
                      <Skeleton height={18} width={130} />
                    ) : (
                      <>
                        <Box
                          style={{
                            flex: '1 0 0',
                            color: 'var(--content-primary)',
                          }}
                          className={classes.utmTableHeaderCell}
                        >
                          {getUnitDisplayName(item.unit_title)}
                        </Box>
                        <Box
                          style={{
                            width: '80px',
                            textAlign: 'right',
                            color: 'var(--content-primary)',
                          }}
                          className={classes.utmTableHeaderCell}
                        >
                          {item.views_count}
                        </Box>
                        <Box
                          style={{
                            width: '140px',
                            textAlign: 'right',
                            color: 'var(--content-primary)',
                          }}
                          className={classes.utmTableHeaderCell}
                        >
                          {item.submit_conversion}
                        </Box>
                      </>
                    )}
                  </Box>
                ))
              ) : (
                <Box className={classes.emptyHeatMapTable}>Nothing found</Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        className="w-full flex gap-2 mt-2 mb-2"
        style={{ borderRadius: '8px' }}
      >
        {!stats.loading &&
          (isEditUnitsMode ? (
            explorerData.units.length > editableItemsPerPage ? (
              <Box className={classes.footer}>
                <IconButton
                  className={classes.footerPart1}
                  onMouseEnter={() => setHoveredArrowLeft(true)}
                  onMouseLeave={() => setHoveredArrowLeft(null)}
                  onClick={() =>
                    handleChangeEditUnitsPage(null, editUnitsPage - 1)
                  }
                  disabled={editUnitsPage === 0}
                >
                  <ArrowLeft
                    color={
                      editUnitsPage === 0
                        ? '#2C313A33'
                        : hoveredArrowLeft
                        ? '#5E59F3'
                        : '#2C313A'
                    }
                  />
                </IconButton>
                <Box className={classes.footerPart2}>
                  <Box className={classes.pagination}>
                    Page {editUnitsPage + 1} of{' '}
                    {Math.ceil(
                      explorerData.units.length / editableItemsPerPage,
                    )}
                  </Box>
                </Box>
                <IconButton
                  className={classes.footerPart3}
                  onMouseEnter={() => setHoveredArrowRight(true)}
                  onMouseLeave={() => setHoveredArrowRight(null)}
                  onClick={() =>
                    handleChangeEditUnitsPage(null, editUnitsPage + 1)
                  }
                  disabled={
                    editUnitsPage >=
                    Math.ceil(
                      explorerData.units.length / editableItemsPerPage,
                    ) -
                      1
                  }
                >
                  <ArrowRight
                    color={
                      editUnitsPage >=
                      Math.ceil(
                        explorerData.units.length / editableItemsPerPage,
                      ) -
                        1
                        ? '#2C313A33'
                        : hoveredArrowRight
                        ? '#5E59F3'
                        : '#2C313A'
                    }
                  />
                </IconButton>
              </Box>
            ) : (
              <></>
            )
          ) : explorer3DOverallStats.units.length > itemsPerPage ? (
            <Box className={classes.footer}>
              <IconButton
                className={classes.footerPart1}
                onMouseEnter={() => setHoveredArrowLeft(true)}
                onMouseLeave={() => setHoveredArrowLeft(null)}
                onClick={() => handleChangePage(null, page - 1)}
                disabled={page === 0}
              >
                <ArrowLeft
                  color={
                    page === 0
                      ? '#2C313A33'
                      : hoveredArrowLeft
                      ? '#5E59F3'
                      : '#2C313A'
                  }
                />
              </IconButton>
              <Box className={classes.footerPart2}>
                <Box className={classes.pagination}>
                  Page {page + 1} of{' '}
                  {Math.ceil(
                    explorer3DOverallStats.units.length / itemsPerPage,
                  )}
                </Box>
              </Box>
              <IconButton
                className={classes.footerPart3}
                onMouseEnter={() => setHoveredArrowRight(true)}
                onMouseLeave={() => setHoveredArrowRight(null)}
                onClick={() => handleChangePage(null, page + 1)}
                disabled={
                  page >=
                  Math.ceil(
                    explorer3DOverallStats.units.length / itemsPerPage,
                  ) -
                    1
                }
              >
                <ArrowRight
                  color={
                    page >=
                    Math.ceil(
                      explorer3DOverallStats.units.length / itemsPerPage,
                    ) -
                      1
                      ? '#2C313A33'
                      : hoveredArrowRight
                      ? '#5E59F3'
                      : '#2C313A'
                  }
                />
              </IconButton>
            </Box>
          ) : (
            <></>
          ))}
      </Box>
    </Page>
  );
};

export default Explorer3DOverviewView;
